/**
 * Monatsübersicht (Spec 8.1): Matrix Mitarbeiter × Betriebstag.
 *
 * Entspricht dem heutigen Monatsblatt. Der Unterschied: hier werden alle Zeilen
 * einer Person summiert, auch wenn sie an einem Tag in drei Bereichen gearbeitet
 * hat. Die Excel-Formel addiert nur erste und letzte Zeile.
 */
import { Fragment, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, type WorkflowDto } from "../lib/api";
import { euro, isZero, money, monthName as monatName, shortDate } from "../lib/format";
import { Amount, Empty, ErrorBox, PageHeader, Spinner, Stat } from "../components/ui";
import { SortKnopf, Suchfeld, ariaSort, useSortierung } from "../components/Sortierung";
import { passtZurSuche, vergleicheBetrag, vergleicheText, vergleicheZahl } from "../lib/sortierung";

interface OverviewRow {
  employeeId: string;
  personalNr: number;
  timeClockId: number | null;
  name: string;
  byDate: Record<string, string>;
  /** Summe je Abrechnungszeitraum — der Betrag, der tatsächlich überwiesen wird. */
  byHalf: Record<string, string>;
  totalAmount: string;
}

interface Half {
  key: string;
  label: string;
  from: string;
  to: string;
}

interface Overview {
  from: string;
  to: string;
  dates: string[];
  totalsByDate: Record<string, string>;
  total: string;
  halves: Half[];
  totalsByHalf: Record<string, string>;
  rows: OverviewRow[];
  openDays: string[];
}

function monthRange(year: number, month: number): { from: string; to: string } {
  const pad = (n: number) => String(n).padStart(2, "0");
  const last = new Date(year, month, 0).getDate();
  return { from: `${year}-${pad(month)}-01`, to: `${year}-${pad(month)}-${pad(last)}` };
}

export function MonthlyOverview() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [anonymous, setAnonymous] = useState(false);
  const [pinned, setPinned] = useState(false);
  // Suchen und sortieren (Rückmeldung Oktober 2026): Name, eine Hälfte oder
  // der Monat; `h:<key>` steht für die Summe einer Hälfte.
  const [suche, setSuche] = useState("");
  const [sortierung, umschalten] = useSortierung<string>({ spalte: "name", richtung: "auf" });
  const range = monthRange(year, month);

  // Der laufende Kalendermonat ist bei einer Umstellung leer. Ohne diesen
  // Sprung sähe die Maske aus, als wären die Daten verschwunden. Startwert ist
  // deshalb der Arbeitsmonat, bis der Nutzer selbst blättert.
  const workflow = useQuery({
    queryKey: ["workflow"],
    queryFn: () => api.get<WorkflowDto>("/workflow"),
  });

  useEffect(() => {
    if (pinned || !workflow.data?.periode) return;
    const [y, m] = workflow.data.periode.month.split("-").map(Number) as [number, number];
    setYear(y);
    setMonth(m);
    setPinned(true);
  }, [workflow.data, pinned]);

  const overview = useQuery({
    queryKey: ["overview", range.from, range.to],
    queryFn: () => api.get<Overview>(`/overview?from=${range.from}&to=${range.to}`),
  });

  const shift = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
    setPinned(true);
  };

  const data = overview.data;
  // Ein Monat sind zwei Abrechnungszeiträume. Für die Monatsauswahl werden sie
  // wieder zusammengefasst; die Tabelle darunter trennt sie wieder.
  const monate = [
    ...new Map(
      (workflow.data?.perioden ?? []).map((p) => [
        p.month,
        { month: p.month, istAltbestand: p.istAltbestand },
      ]),
    ).values(),
  ];
  /** Der Zeitraum, der gerade zur Auszahlung ansteht. Er wird hervorgehoben. */
  const aktuellePeriode = workflow.data?.periode?.key ?? null;
  /**
   * Verbindlich ist ein Betrag erst, wenn die Auszahlungsperiode gesperrt ist.
   * Davor „in Vorbereitung", ohne Periode „Vorschau" — „wird ausgezahlt"
   * hätte eine Sicherheit behauptet, die es noch nicht gibt.
   */
  const periodenStand = new Map(
    (workflow.data?.perioden ?? []).map((p) => [
      p.key,
      {
        auszahlungStatus: p.auszahlungStatus,
        offen: p.entwurf + p.berechnet,
        altbestand: p.istAltbestand && p.offeneTage === 0,
      },
    ]),
  );
  const monthName = new Date(year, month - 1, 1).toLocaleDateString("de-DE", {
    month: "long",
    year: "numeric",
  });

  return (
    <>
      <PageHeader
        label="Monatsübersicht"
        title={monthName}
        actions={
          <>
            <button className="btn-ghost" onClick={() => shift(-1)}>
              ← Vormonat
            </button>
            <button className="btn-ghost" onClick={() => shift(1)}>
              Folgemonat →
            </button>
            <button
              className={anonymous ? "btn-primary" : "btn-ghost"}
              onClick={() => setAnonymous((v) => !v)}
              title="Zeigt statt Namen die Stempeluhr-IDs, wie im Aushang"
            >
              Anonym
            </button>
            <Link
              className="btn-ghost"
              to={`/aushang?monat=${year}-${String(month).padStart(2, "0")}`}
              title="Einseitige Übersicht je Stempeluhr-ID, druckfertig"
            >
              Aushang
            </Link>
            <a
              className="btn-ghost"
              href={`/api/v1/overview/export?from=${range.from}&to=${range.to}`}
            >
              CSV
            </a>
          </>
        }
      >
        Jede Zeile ist eine Person, jede Spalte ein Betriebstag. Abgerechnet wird
        halbmonatlich; die hervorgehobene Spalte ist der Betrag der Auszahlung. Solange
        die Auszahlungsperiode nicht gesperrt ist, sind die Zahlen vorläufig und können
        sich noch ändern.
      </PageHeader>

      {monate.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {monate.map((m) => {
            const aktiv = m.month === `${year}-${String(month).padStart(2, "0")}`;
            return (
              <button
                key={m.month}
                className={aktiv ? "btn-primary" : "btn-ghost"}
                onClick={() => {
                  const [y, mo] = m.month.split("-").map(Number) as [number, number];
                  setYear(y);
                  setMonth(mo);
                  setPinned(true);
                }}
                title={m.istAltbestand ? "Aus der alten Excel übernommen." : undefined}
              >
                {monatName(m.month)}
                {m.istAltbestand ? " · Altbestand" : ""}
              </button>
            );
          })}
        </div>
      ) : null}

      {overview.isLoading ? <Spinner label="Monat wird geladen" /> : null}
      {overview.error ? <ErrorBox error={overview.error} /> : null}

      {data ? (
        <>
          <div className="mb-6 flex flex-wrap gap-6">
            <Stat label="Summe" value={euro(data.total)} />
            <Stat label="Personen" value={String(data.rows.length)} />
            <Stat label="Tage mit Auszahlung" value={String(data.dates.length)} />
            <Stat
              label="nicht freigegeben"
              value={String(data.openDays.length)}
              tone={data.openDays.length > 0 ? "alert" : "ok"}
            />
          </div>

          {data.openDays.length > 0 ? (
            <div className="mb-6 rounded border border-warn/30 bg-warn/5 px-4 py-3 text-sm">
              {data.openDays.length} Tag(e) mit Trinkgeld sind noch nicht freigegeben und
              zählen deshalb nicht in eine Auszahlung:{" "}
              {data.openDays.map((d, i) => (
                <span key={d}>
                  {i > 0 ? ", " : ""}
                  <Link to={`/betriebstage/${d}`} className="underline hover:text-ink">
                    {shortDate(d)}
                  </Link>
                </span>
              ))}
            </div>
          ) : null}

          {data.rows.length > 0 ? (
            <div className="mb-3 flex justify-end">
              <Suchfeld
                wert={suche}
                onChange={setSuche}
                platzhalter={anonymous ? "Stempeluhr-ID suchen" : "Name, PersNr oder Stempeluhr-ID suchen"}
              />
            </div>
          ) : null}

          {data.rows.length === 0 ? (
            <Empty>
              Für diesen Monat ist noch nichts berechnet. Die Übersicht füllt sich, sobald
              Betriebstage gerechnet und freigegeben sind.
            </Empty>
          ) : (
            <div className="card overflow-x-auto">
              <table className="tbl">
                <thead>
                  {/*
                    * Eigene Kopfzeile je Halbmonat. Der Monat ist zwei
                    * Auszahlungen; ohne diese Zeile sieht man nur eine Reihe
                    * Datumsspalten und muss die Grenze suchen.
                    */}
                  <tr>
                    <th className="sticky left-0 z-10 bg-surface" />
                    {data.halves.map((h) => {
                      const tage = data.dates.filter((d) => d >= h.from && d <= h.to);
                      const laufend = h.key === aktuellePeriode;
                      return (
                        <th
                          key={h.key}
                          colSpan={tage.length + 1}
                          className={`border-l-2 text-center ${
                            laufend
                              ? "border-accent bg-accent/10 !text-accent"
                              : "border-ink/30 bg-surface"
                          }`}
                        >
                          {h.label}
                          <span className="ml-2 normal-case">
                            {shortDate(h.from)}–{shortDate(h.to)}
                          </span>
                          {(() => {
                            const stand = periodenStand.get(h.key);
                            if (!stand) return null;
                            // Aus Excel übernommen heißt: über Excel längst ausgezahlt.
                            // „Vorschau" wäre hier falsch.
                            if (stand.altbestand && !stand.auszahlungStatus) {
                              return <span className="ml-2 normal-case">· aus Excel übernommen, ausgezahlt</span>;
                            }
                            // Endgültig klingt es erst, wenn es endgültig ist.
                            if (stand.auszahlungStatus) {
                              const text = {
                                OPEN: "Auszahlung in Vorbereitung",
                                LOCKED: "gesperrt, wird ausgezahlt",
                                EXPORTED: "zur Überweisung exportiert",
                                PAID: "ausgezahlt",
                              }[stand.auszahlungStatus];
                              return <span className="ml-2 normal-case">· {text}</span>;
                            }
                            if (!laufend) return null;
                            return (
                              <span className="ml-2 normal-case">
                                · Vorschau
                                {stand.offen > 0
                                  ? `, ${stand.offen} ${stand.offen === 1 ? "Tag" : "Tage"} noch nicht freigegeben`
                                  : ", Auszahlung noch nicht angelegt"}
                              </span>
                            );
                          })()}
                        </th>
                      );
                    })}
                    <th className="border-l-2 border-ink/30 bg-surface" />
                  </tr>
                  <tr>
                    <th
                      className="sticky left-0 z-10 bg-surface"
                      aria-sort={ariaSort(sortierung.spalte === "name", sortierung.richtung)}
                    >
                      <SortKnopf
                        aktiv={sortierung.spalte === "name"}
                        richtung={sortierung.richtung}
                        onClick={() => umschalten("name", "auf")}
                      >
                        {anonymous ? "Stempeluhr-ID" : "Name"}
                      </SortKnopf>
                      <span className="ml-2 normal-case text-muted">· Beträge in €</span>
                    </th>
                    {data.halves.map((h) => (
                      <Fragment key={h.key}>
                        {data.dates
                          .filter((d) => d >= h.from && d <= h.to)
                          .map((d, i) => (
                            <th
                              key={d}
                              className={i === 0 ? "border-l-2 border-ink/30 text-right" : "text-right"}
                            >
                              <Link to={`/betriebstage/${d}`} className="hover:text-accent">
                                {shortDate(d)}
                              </Link>
                            </th>
                          ))}
                        <th
                          className={`text-right ${
                            h.key === aktuellePeriode ? "bg-accent/10 !text-accent" : "bg-surface"
                          }`}
                          title={`Summe ${h.label}: der Betrag, der überwiesen wird`}
                          aria-sort={ariaSort(sortierung.spalte === `h:${h.key}`, sortierung.richtung)}
                        >
                          <SortKnopf
                            aktiv={sortierung.spalte === `h:${h.key}`}
                            richtung={sortierung.richtung}
                            onClick={() => umschalten(`h:${h.key}`, "ab")}
                          >
                            Summe
                          </SortKnopf>
                        </th>
                      </Fragment>
                    ))}
                    <th
                      className="border-l border-ink/20 text-right"
                      aria-sort={ariaSort(sortierung.spalte === "monat", sortierung.richtung)}
                    >
                      <SortKnopf
                        aktiv={sortierung.spalte === "monat"}
                        richtung={sortierung.richtung}
                        onClick={() => umschalten("monat", "ab")}
                      >
                        Monat
                      </SortKnopf>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows
                    .filter((row) =>
                      anonymous
                        ? passtZurSuche(suche, row.timeClockId)
                        : passtZurSuche(suche, row.name, row.personalNr, row.timeClockId),
                    )
                    .sort((a, b) => {
                      const { spalte, richtung } = sortierung;
                      if (spalte === "monat") return vergleicheBetrag(a.totalAmount, b.totalAmount, richtung);
                      if (spalte.startsWith("h:")) {
                        const key = spalte.slice(2);
                        return vergleicheBetrag(a.byHalf[key] ?? null, b.byHalf[key] ?? null, richtung);
                      }
                      return anonymous
                        ? vergleicheZahl(a.timeClockId, b.timeClockId, richtung)
                        : vergleicheText(a.name, b.name, richtung);
                    })
                    .map((row) => (
                    <tr key={row.employeeId}>
                      <td className="sticky left-0 z-10 whitespace-nowrap bg-page">
                        {anonymous ? (
                          <span className="font-mono">{row.timeClockId ?? "–"}</span>
                        ) : (
                          <>
                            {row.name}
                            <span className="ml-2 font-mono text-xs text-muted">
                              {row.personalNr}
                            </span>
                          </>
                        )}
                      </td>
                      {data.halves.map((h) => (
                        <Fragment key={h.key}>
                          {data.dates
                            .filter((d) => d >= h.from && d <= h.to)
                            .map((d, i) => {
                              const value = row.byDate[d];
                              return (
                                <td
                                  key={d}
                                  className={`whitespace-nowrap ${
                                    i === 0 ? "border-l-2 border-ink/30" : ""
                                  }`}
                                >
                                  <Amount value={value ? money(value) : "–"} muted={!value} />
                                </td>
                              );
                            })}
                          <td
                            className={`whitespace-nowrap font-medium ${
                              h.key === aktuellePeriode ? "bg-accent/10" : "bg-surface/60"
                            }`}
                          >
                            {/* Eine 0,00 unterscheidet sich optisch nicht von
                                einem echten Betrag; wer in dieser Hälfte nicht
                                gearbeitet hat, bekommt einen Strich. */}
                            <Amount
                              value={isZero(row.byHalf[h.key]) ? "–" : money(row.byHalf[h.key] ?? "")}
                              muted={isZero(row.byHalf[h.key])}
                            />
                          </td>
                        </Fragment>
                      ))}
                      <td className="whitespace-nowrap border-l-2 border-ink/30 text-muted">
                        <Amount value={money(row.totalAmount)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="sticky left-0 z-10 bg-surface font-medium">Gesamt</td>
                    {data.halves.map((h) => (
                      <Fragment key={h.key}>
                        {data.dates
                          .filter((d) => d >= h.from && d <= h.to)
                          .map((d, i) => (
                            <td
                              key={d}
                              className={`whitespace-nowrap bg-surface ${
                                i === 0 ? "border-l-2 border-ink/30" : ""
                              }`}
                            >
                              <Amount value={money(data.totalsByDate[d])} />
                            </td>
                          ))}
                        <td
                          className={`whitespace-nowrap font-medium ${
                            h.key === aktuellePeriode
                              ? "bg-accent/20 text-accent"
                              : "bg-surface font-medium"
                          }`}
                        >
                          <Amount value={money(data.totalsByHalf[h.key] ?? "0.00")} />
                        </td>
                      </Fragment>
                    ))}
                    <td className="whitespace-nowrap border-l-2 border-ink/30 bg-surface text-muted">
                      <Amount value={money(data.total)} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
